<template>
  <v-form ref="form">
    <v-card outlined>
      <v-expansion-panels flat accordion>
        <v-expansion-panel>
          <v-expansion-panel-header>Configuration Guide</v-expansion-panel-header>
          <v-expansion-panel-content>
            <p class="text-body-2">
              The offer configuration section is used to determine the best offer to use in the Offer CTAs for a page.
            </p>
            <p class="text-body-2">
              An offer type is required before customising filters. This is because the offer type dictates which
              properties can be filtered.
            </p>
            <p class="text-body-2">
              Each property has a defined type such as text, number, date, etc. This type determines which filters and
              values can be used.
            </p>
            <p class="text-body-2">
              For an explanation of what the filters do and which types of data they are compatible with, click
              <a
                href="https://academy.investoogroup.com/wiki/database-filters-in-layman-terms/"
                target="_blank"
                rel="noopener noreferrer"
                >here</a
              >.
            </p>
            <p class="text-body-2">
              As well as a fixed value, you can choose to use an Entity Type attached to a page to filter by. For
              example, selecting "Crypto Coin" will filter by the coin attached to a page e.g, Bitcoin, without needing
              to set a specific coin here.
            </p>
          </v-expansion-panel-content>
        </v-expansion-panel>
      </v-expansion-panels>
      <v-divider />

      <div v-if="!config" class="ma-6">
        <v-btn color="primary" block depressed @click="emitBlankConfig">Begin Offer Configuration</v-btn>
      </div>

      <template v-if="config">
        <v-stepper :value="stepper" flat vertical>
          <v-stepper-step :complete="stepper > 1" :step="1">
            <div class="d-flex align-center">
              <span>Select an Offer Type</span>
              <v-spacer></v-spacer>
              <more-menu>
                <v-list-item :readonly="!isPermittedTo('update')" @click="showRemoveConfigModal = true">
                  <v-list-item-title>
                    <v-icon left small>delete</v-icon>
                    <span>Remove Config</span>
                  </v-list-item-title>
                </v-list-item>
              </more-menu>
            </div>
          </v-stepper-step>
          <v-stepper-content class="stepper-content--show" :step="1">
            <select-list
              :items="offerTypeOptions"
              :persistent-hint="!!config.offerType"
              :readonly="!isPermittedTo('update')"
              :value="config.offerType"
              class="mt-1"
              hint="Note: Changing the Offer Type will reset this configuration."
              label="Offer Type"
              required
              @input="onInput($event, 'offerType')"
            />
          </v-stepper-content>

          <v-stepper-step :complete="stepper > 2" :step="2">Configure Offer</v-stepper-step>
          <v-stepper-content :class="{ 'stepper-content--show': stepper > 1 }" :step="2">
            <v-row class="mt-0">
              <v-col cols="12">
                <true-false-list
                  :readonly="!isPermittedTo('update')"
                  :show-unset="false"
                  :value="config.allowSwaps"
                  hint="If offers for a coin are unavailable, show offers that support withdrawing to a wallet and swapping on a Decentralised Exchange"
                  label="Allow Swaps"
                  persistent-hint
                  required
                  @input="onInput($event, 'allowSwaps')"
                />
              </v-col>
              <v-col cols="12">
                <true-false-list
                  :readonly="!isPermittedTo('update')"
                  :show-unset="false"
                  :value="config.includeDrafts"
                  hint="Include offers that may not published and ready for commercial use."
                  label="Include Drafts"
                  persistent-hint
                  required
                  @input="onInput($event, 'includeDrafts')"
                />
              </v-col>
              <v-col cols="12">
                <true-false-list
                  :readonly="!isPermittedTo('update')"
                  :show-unset="false"
                  :value="config.showRestricted"
                  hint="Include offers that may be restricted in a user's country."
                  label="Show Restricted"
                  persistent-hint
                  required
                  @input="onInput($event, 'showRestricted')"
                />
              </v-col>
              <v-col cols="12">
                <text-input
                  :max="5"
                  :min="1"
                  :readonly="!isPermittedTo('update')"
                  :value="config.perPage"
                  hint="Number of offers to return. Must be a value between 1 and 5."
                  label="Offer Limit"
                  type="number"
                  persistent-hint
                  required
                  @input="onInput($event, 'perPage')"
                />
              </v-col>
            </v-row>
          </v-stepper-content>

          <v-stepper-step :step="3">Customise Filters</v-stepper-step>
          <template v-if="config.filters && config.filters.length">
            <v-stepper-content :class="{ 'stepper-content--show': stepper > 2 }" class="ml-0" :step="3">
              <v-row v-for="(item, index) in config.filters" :key="index" :class="{ 'mt-1': index === 0 }" class="ml-0">
                <v-col v-if="index !== 0" cols="12">
                  <v-divider />
                </v-col>
                <v-col cols="12">
                  <v-row>
                    <v-col sm="4" class="d-flex flex-column pl-2">
                      <v-tooltip top max-width="300">
                        <template #activator="{ on }">
                          <div v-on="on">
                            <toggle-input
                              :is-disabled="isFilterFixedValue(item.filter)"
                              :readonly="!isPermittedTo('update')"
                              :value="item.useEntityType"
                              class="mt-2"
                              label="Use Page Entity"
                              @input="onFilterInput($event, 'useEntityType', index)"
                            />
                          </div>
                        </template>
                        <div>
                          <div>
                            Enabling "Use Page Entity" allows you to select an entity attached to a page to use as a
                            filter instead of a fixed value.
                          </div>
                          <div v-if="isFilterFixedValue(item.filter)" class="mt-2">
                            An entity cannot be used if Filter is set to a fixed value i.e, N/A
                          </div>
                        </div>
                      </v-tooltip>
                      <v-spacer />
                      <v-sheet height="40" class="d-flex justify-space-between align-center">
                        <v-tooltip top>
                          <template #activator="{ on }">
                            <v-btn
                              :disabled="!isPermittedTo('update')"
                              color="error"
                              depressed
                              small
                              v-on="on"
                              @click="onRemoveFilter(index)"
                            >
                              <v-icon small>delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Remove filter</span>
                        </v-tooltip>
                        <v-tooltip top>
                          <template #activator="{ on }">
                            <v-progress-circular
                              color="primary"
                              :value="getPropertyProgress(item)"
                              rotate="-90"
                              v-on="on"
                            >
                              <v-icon v-if="getPropertyProgress(item) === 100" small>check</v-icon>
                            </v-progress-circular>
                          </template>
                          <div>
                            <span v-if="getPropertyProgress(item) === 100">Filter ready</span>
                            <span v-else>Filter incomplete</span>
                          </div>
                        </v-tooltip>
                      </v-sheet>
                    </v-col>
                    <v-col sm="8" class="pl-0">
                      <v-row>
                        <v-col sm="12">
                          <select-list
                            :items="propertyOptions"
                            :readonly="!isPermittedTo('update')"
                            :value="item.property"
                            label="Property"
                            hide-details
                            required
                            @input="onFilterInput($event, 'property', index)"
                          />
                        </v-col>
                        <v-col sm="12">
                          <select-list
                            :disabled="!item.property"
                            :items="getPropertyFilterOptions(mergedSchema[item.property], item.useEntityType)"
                            :readonly="!isPermittedTo('update')"
                            :value="item.filter"
                            custom-type="description"
                            item-text="text"
                            item-value="key"
                            label="Filter"
                            clearable
                            hide-details
                            required
                            return-object
                            @input="onFilterInput($event, 'filter', index)"
                          />
                        </v-col>
                        <v-col sm="12">
                          <select-list
                            v-if="item.useEntityType && !isFilterFixedValue(item.filter)"
                            :disabled="!item.property || !item.filter || isFilterFixedValue(item.filter)"
                            :items="entityTypes"
                            :limit="2"
                            :readonly="!isPermittedTo('update')"
                            :value="item.entityTypePageTypeId"
                            label="Page Entities"
                            hide-details
                            multiple
                            required
                            @input="onFilterInput($event, 'entityTypePageTypeId', index)"
                          />
                          <component
                            :is="getComponent(mergedSchema[item.property], item.filter)"
                            v-else
                            ref="required"
                            :value="item.value"
                            :disabled="!item.property || !item.filter || isFilterFixedValue(item.filter)"
                            :items="getComponentItems(mergedSchema[item.property], item.filter)"
                            :limit="1"
                            :multiple="
                              getPropertyType(mergedSchema[item.property]) === 'array' &&
                                !isFilterFixedValue(item.filter)
                            "
                            :readonly="!isPermittedTo('update')"
                            :show-unset="false"
                            :type="getPropertyType(mergedSchema[item.property])"
                            class="flex-grow-1"
                            label="Value"
                            hide-details
                            @input="onFilterInput($event, 'value', index)"
                          />
                        </v-col>
                      </v-row>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-stepper-content>
          </template>
          <div v-else class="text-caption ml-15">
            No filters applied. Click 'Add Filters' to refine offers by properties.
          </div>
        </v-stepper>
        <v-card-actions v-if="stepper > 2" class="pt-0 justify-end">
          <v-btn :disabled="!isPermittedTo('update')" color="primary" depressed small @click="onAddFilter">
            <v-icon left>add</v-icon>
            <span>Add Filter</span>
          </v-btn>
        </v-card-actions>
      </template>
    </v-card>

    <generic-modal
      v-model="showRemoveConfigModal"
      title="Remove Offer Configuration"
      cancel-color="grey"
      confirm-color="error"
      hide-louis
      @modal-confirm="removeOfferConfig"
    >
      Are you sure you want to remove this offer configuration? This will affect the offers shown on all pages belonging
      to this page type!
    </generic-modal>
  </v-form>
</template>

<script>
// Mixins
import PermissionsHandler from '@/mixins/permissions-handler'
// Helpers
import { mapGetters, mapState } from 'vuex'
import { getDeepestProperties } from '@/utils/parse-schema'
import { startCase } from 'lodash-es'
import isSet from '@/utils/is-set'
// Components
import GenericModal from '@/components/modals/generic-modal'
import MoreMenu from '@/components/more-menu'
import SelectList from '@/components/forms/select-list'
import ToggleInput from '@/components/forms/toggle-input'
import TrueFalseList from '@/components/forms/true-false-list'

const blankConfig = () => ({
  allowSwaps: null,
  filters: [],
  includeDrafts: null,
  offerType: null,
  showRestricted: null,
  perPage: 1,
})

const blankFilterObject = ({
  entityTypePageTypeId = [],
  filter = null,
  property = null,
  value = null,
  useEntityType = false,
} = {}) => ({
  entityTypePageTypeId,
  filter,
  property,
  useEntityType,
  value,
})

export default {
  name: 'OfferConfiguration',
  components: {
    DatePicker: () => import('@/components/forms/date-picker'),
    DatetimePicker: () => import('@/components/forms/datetime-picker'),
    GenericModal,
    MoreMenu,
    OffersList: () => import('@/views/offers/components/offers-list'),
    SelectList,
    TextInput: () => import('@/components/forms/text-input'),
    ToggleInput,
    TrueFalseList,
  },
  mixins: [PermissionsHandler],
  model: {
    prop: 'config',
  },
  props: {
    config: {
      type: Object,
      default: () => blankConfig(),
    },
    entityTypes: {
      type: Array,
      default: () => [],
    },
  },
  data: () => ({
    showRemoveConfigModal: false,
  }),
  computed: {
    ...mapState({
      filters: state => state.options.options.filters,
      offersSchemas: state => state.offers.schema,
      partnerSchema: state => state.partners.schema.properties,
    }),
    ...mapGetters({
      getItemsForOption: 'options/getItemsForOption',
    }),
    filterOptions() {
      return this.filters.map(({ key, type, compatibility, description }) => ({
        text: startCase(key),
        key,
        type,
        compatibility,
        description,
      }))
    },
    mergedSchema() {
      return { ...this.offerSchema, ...this.partnerSchema, slug: { key: 'slug', title: 'Slug', type: 'string' } }
    },
    offerSchema() {
      if (!this.config.offerType) return {}
      return this.offersSchemas[this.config.offerType].properties
    },
    offerTypeOptions() {
      return this.getItemsForOption('offerTypes')
    },
    propertyOptions() {
      return Object.values(this.mergedSchema)
        .map(({ title, key }) => {
          return {
            key: title || startCase(key),
            value: key,
          }
        })
        .sort((a, b) => a.key.localeCompare(b.key))
    },
    stepper() {
      if (!this.config || !this.config.offerType) return 1
      if (
        !isSet(this.config.allowSwaps) ||
        !isSet(this.config.includeDrafts) ||
        !isSet(this.config.showRestricted) ||
        !isSet(this.config.perPage)
      )
        return 2
      return 3
    },
  },
  methods: {
    emitBlankConfig() {
      this.$emit('input', blankConfig())
    },
    getComponent(property, filter) {
      if (!property) return 'text-input'

      const { format, items, meta: { option } = {} } = getDeepestProperties(property)
      if (items || option || this.isFilterFixedValue(filter)) return 'SelectList'
      if (format === 'date') return 'DatePicker'
      if (format === 'date-time') return 'DatetimePicker'
      const type = this.getPropertyType(property)
      if (type === 'boolean') return 'TrueFalseList'
      return 'TextInput'
    },
    getComponentItems(property, filter) {
      if (!property) return []

      if (this.isFilterFixedValue(filter)) return [{ key: 'N/A', value: true }]
      const { meta: { option } = {} } = getDeepestProperties(property)
      if (!option) return []
      return this.getItemsForOption(option)
    },
    getPropertyType(property) {
      const { type } = getDeepestProperties(property)
      if (Array.isArray(type)) return type.find(t => t !== null)
      return type
    },
    getPropertyFilterOptions(property, multiple = false) {
      const type = multiple ? 'array' : this.getPropertyType(property)
      return this.filterOptions.filter(option => option.compatibility.includes(type))
    },
    getPropertyProgress({ useEntityType, property, value, entityTypePageTypeId, filter }) {
      const item = { property, filter, value: useEntityType ? entityTypePageTypeId : value }
      return Math.ceil((Object.values(item).filter(v => isSet(v)).length / Object.keys(item).length) * 100)
    },
    getUpdatedFilterObject(value, model, index) {
      const currentFilterObject = this.config.filters[index]

      switch (model) {
        case 'property':
          const prevProperty =
            currentFilterObject.property && this.getPropertyType(this.mergedSchema[currentFilterObject.property])
          const newPropertyType = this.getPropertyType(this.mergedSchema[value])
          const isSameType = prevProperty === newPropertyType
          return {
            ...currentFilterObject,
            property: value,
            filter: isSameType ? currentFilterObject.filter : null,
            value: null,
            entityTypePageTypeId: null,
          }
        case 'filter':
          return {
            ...currentFilterObject,
            [model]: value,
            value: this.isFilterFixedValue(value) || null,
            useEntityType: this.isFilterFixedValue(value) ? false : currentFilterObject.useEntityType,
          }
        case 'useEntityType':
          return {
            ...currentFilterObject,
            [model]: value,
            entityTypePageTypeId: null,
            value: null,
          }
        default:
          return { ...currentFilterObject, [model]: value }
      }
    },
    isFilterFixedValue(filter) {
      if (!filter) return false
      return ['IsSet', 'NotSet', 'has', 'hasNo'].includes(filter.key)
    },
    onAddFilter() {
      this.$emit('input', { ...this.config, filters: [...this.config.filters, blankFilterObject()] })
    },
    onFilterInput(value, model, index) {
      const filters = [
        ...this.config.filters.slice(0, index),
        this.getUpdatedFilterObject(value, model, index),
        ...this.config.filters.slice(index + 1),
      ]
      this.$emit('input', { ...this.config, filters })
    },
    onInput(value, model) {
      this.$emit('input', { ...this.config, [model]: value, filters: model === 'offerType' ? [] : this.config.filters })
    },
    onRemoveFilter(index) {
      const filters = [...this.config.filters.slice(0, index), ...this.config.filters.slice(index + 1)]
      this.$emit('input', { ...this.config, filters })
    },
    removeOfferConfig() {
      this.$emit('input', null)
      this.showRemoveConfigModal = false
    },
  },
}
</script>

<style lang="scss" scoped>
.stepper-content--show ::v-deep .v-stepper__wrapper {
  height: auto !important;
}
</style>
